import classNames from 'classnames';
import React, { useLayoutEffect, useRef, useState } from 'react';
import ButtonComponent from '../../components/Button';
import { useQuill } from '../QuillContext';
import * as styles from './Button.module.scss';
import useButtonAction from './useButtonAction';

const HORIZONTAL_PADDING = parseInt(styles.horizontalPadding, 10);

interface ButtonInputProps {
  buttonText?: string;
  placeholder: string;
  onChange: (text: string) => void;
}

const ButtonInput = ({
  buttonText,
  placeholder,
  onChange,
}: ButtonInputProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { quill } = useQuill();
  const textRef = useRef<HTMLSpanElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [width, setWidth] = useState(0);
  const [value, setValue] = useState(buttonText || '');

  const actualButtonText = buttonText || placeholder;

  useLayoutEffect(() => {
    setWidth(textRef.current?.getBoundingClientRect().width || 0);
    setContainerWidth(containerRef.current?.getBoundingClientRect().width || 0);
  }, [value]);

  const handleChange = (nextValue: string) => {
    setValue(nextValue);
    onChange(nextValue);
  };

  const padding =
    width + HORIZONTAL_PADDING * 2 < containerWidth
      ? (containerWidth - width) / 2
      : undefined;

  return (
    <div ref={containerRef} className={styles.buttonInput}>
      <span className={styles.text} ref={textRef}>
        {value || actualButtonText}
      </span>
      <input
        ref={inputRef}
        type="text"
        value={value}
        style={{ width, paddingLeft: padding, paddingRight: padding }}
        onChange={(e) => {
          handleChange(e.target.value);
        }}
        placeholder={placeholder}
        aria-label="Edit Button Text"
        onBlur={() => {
          handleChange(value.trim());
        }}
        onCut={(e) => {
          e.stopPropagation();
        }}
        onCopy={(e) => {
          e.stopPropagation();
        }}
        onPaste={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Tab') {
            e.preventDefault();
            const selection = quill?.getSelection();
            if (selection) {
              quill?.setSelection(selection.index + 1, 0);
            }
          }

          if (e.key !== 'Enter') {
            e.stopPropagation();
          }
        }}
      />
    </div>
  );
};

interface ButtonProps {
  buttonText?: string;
  type: 'action' | 'navigation';
  destination: string;
  onButtonTextChange: (text: string) => void;
}

const Button = ({
  buttonText,
  type,
  destination,
  onButtonTextChange,
}: ButtonProps) => {
  const performButtonAction = useButtonAction();
  const { readOnly } = useQuill();
  const placeholder = 'Click here';

  return (
    <div className={styles.container}>
      <div className={classNames('button-container', styles.buttonContainer)}>
        {readOnly ? (
          <ButtonComponent
            variant="primary"
            size="large"
            onClick={(e) => {
              if (!readOnly || type === 'action') {
                e.preventDefault();
              }

              if (type === 'action') {
                performButtonAction(destination);
              }
            }}
            to={type === 'navigation' ? destination : undefined}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className={styles.text}>{buttonText || placeholder}</div>
          </ButtonComponent>
        ) : (
          <ButtonInput
            buttonText={buttonText}
            placeholder={placeholder}
            onChange={onButtonTextChange}
          />
        )}
      </div>
    </div>
  );
};

export default Button;
